import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Mail, Phone, MapPin, ExternalLink, Clock, ChevronRight, Heart, Home } from 'lucide-react';
import { getBreadcrumbSchema } from '@/utils/structuredData';

// Breadcrumb configuration for all pages
const breadcrumbConfig = {
  '/': null, // No breadcrumbs on homepage
  '/weg-verwaltung': [
    { label: 'Startseite', url: '/' },
    { label: 'WEG-Verwaltung' }
  ],
  '/mietverwaltung': [
    { label: 'Startseite', url: '/' },
    { label: 'Mietverwaltung' }
  ],
  '/sondereigentumsverwaltung': [
    { label: 'Startseite', url: '/' },
    { label: 'Sondereigentumsverwaltung' }
  ],
  '/facility': [
    { label: 'Startseite', url: '/' },
    { label: 'Dienstleistungen' }
  ],
  '/kontakt': [
    { label: 'Startseite', url: '/' },
    { label: 'Kontakt & Anfrage' }
  ],
  '/hausverwaltung-wiesbaden': [
    { label: 'Startseite', url: '/' },
    { label: 'Standorte' },
    { label: 'Hausverwaltung Wiesbaden' }
  ],
  '/hausverwaltung-mainz': [
    { label: 'Startseite', url: '/' },
    { label: 'Standorte' },
    { label: 'Hausverwaltung Mainz' }
  ],
  '/hausverwaltung-rhein-main': [
    { label: 'Startseite', url: '/' },
    { label: 'Standorte' },
    { label: 'Hausverwaltung Rhein-Main' }
  ],
  '/hausverwaltung-idstein': [
    { label: 'Startseite', url: '/' },
    { label: 'Standorte' },
    { label: 'Hausverwaltung Idstein' }
  ],
  '/hausverwaltung-hochheim': [
    { label: 'Startseite', url: '/' },
    { label: 'Standorte' },
    { label: 'Hausverwaltung Hochheim' }
  ],
  '/verwalter-wechseln': [
    { label: 'Startseite', url: '/' },
    { label: 'Verwalter wechseln' }
  ],
  '/ankauf-verkauf': [
    { label: 'Startseite', url: '/' },
    { label: 'Ankauf & Verkauf' }
  ],
  '/referenzen': [
    { label: 'Startseite', url: '/' },
    { label: 'Referenzen' }
  ],
  '/aktuelles': [
    { label: 'Startseite', url: '/' },
    { label: 'Aktuelles' }
  ],
  '/impressum': [
    { label: 'Startseite', url: '/' },
    { label: 'Impressum' }
  ],
  '/datenschutz': [
    { label: 'Startseite', url: '/' },
    { label: 'Datenschutzerklärung' }
  ],
};

// Breadcrumb component rendered above footer
const FooterBreadcrumbs = () => {
  const location = useLocation();
  const items = breadcrumbConfig[location.pathname];

  // Don't show breadcrumbs on homepage, portal pages, or if no config exists
  if (!items || location.pathname === '/' || location.pathname.startsWith('/portal')) return null;

  // Convert to schema format
  const schemaItems = items.map(item => ({
    name: item.label,
    url: item.url
  }));

  return (
    <>
      {/* JSON-LD Schema for SEO */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(getBreadcrumbSchema(schemaItems))}
        </script>
      </Helmet>

      {/* Visual Breadcrumbs */}
      <div className="bg-gray-100 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <nav aria-label="Breadcrumb" data-testid="breadcrumbs">
            <ol 
              className="flex flex-wrap items-center gap-1.5 text-sm"
              itemScope 
              itemType="https://schema.org/BreadcrumbList"
            >
              {items.map((item, index) => {
                const isLast = index === items.length - 1;
                const isFirst = index === 0;

                return (
                  <li 
                    key={index} 
                    className="flex items-center"
                    itemProp="itemListElement" 
                    itemScope 
                    itemType="https://schema.org/ListItem"
                  >
                    {!isFirst && (
                      <ChevronRight className="w-3.5 h-3.5 text-gray-400 mx-1.5 flex-shrink-0" />
                    )}

                    {isLast ? (
                      <span 
                        className="text-brand-navy font-medium truncate max-w-[200px]"
                        itemProp="name"
                        aria-current="page"
                      >
                        {item.label}
                      </span>
                    ) : (
                      <Link
                        to={item.url || '/'}
                        className="text-gray-500 hover:text-brand-gold transition-colors flex items-center gap-1.5 truncate max-w-[150px]"
                        itemProp="item"
                      >
                        {isFirst && <Home className="w-3.5 h-3.5 flex-shrink-0" />}
                        <span itemProp="name">{item.label}</span>
                      </Link>
                    )}
                    <meta itemProp="position" content={String(index + 1)} />
                  </li>
                );
              })}
            </ol>
          </nav>
        </div>
      </div>
    </>
  );
};

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <>
      {/* Breadcrumbs above footer */}
      <FooterBreadcrumbs />
      
      <footer data-testid="main-footer" className="bg-gradient-to-b from-brand-navy to-brand-navy-dark">
      {/* Certification Section */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            <picture>
              <source srcSet="/images/zertifiziert-weiss.webp" type="image/webp" />
              <img 
                src="/images/zertifiziert-weiss.png" 
                alt="Zertifizierter Verwalter (IHK) gemäß § 26a WEG" 
                width="1024"
                height="1024"
                loading="lazy"
                className="h-32 lg:h-40 w-auto flex-shrink-0"
              />
            </picture>
            <div className="flex-1 text-center lg:text-left">
              <h3 className="text-brand-gold font-bold text-2xl lg:text-3xl mb-2">
                Zertifizierter Verwalter (IHK)
              </h3>
              <p className="text-white/90 text-lg mb-3">gemäß § 26a WEG</p>
              <p className="text-white/60 text-sm max-w-xl mb-4">
                Seit der WEG-Reform 2020 haben Eigentümer das Recht auf einen zertifizierten Verwalter. 
                Wir erfüllen alle Anforderungen und sind IHK-geprüft – für Ihre Sicherheit.
              </p>
              <Link 
                to="/zertifizierter-verwalter"
                className="inline-flex items-center gap-2 text-brand-gold hover:text-brand-gold-light text-sm font-medium transition-colors"
              >
                Was bedeutet das?
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <Link 
              to="/kontakt"
              className="group flex items-center gap-3 bg-brand-gold hover:bg-[#d4962f] text-brand-navy px-6 py-3 rounded-lg font-semibold transition-all flex-shrink-0"
            >
              Kostenlose Beratung
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-6">
          
          {/* Company Info - 3 cols */}
          <div className="col-span-2 lg:col-span-3">
            <div className="flex items-center gap-3 mb-5">
              <img 
                src="/images/logo.png" 
                alt="Logo Scholze-Kurz & Kurz Immobilien GmbH – Hausverwaltung Wiesbaden Mainz seit 1981" 
                title="Scholze-Kurz & Kurz Immobilien GmbH – Hausverwaltung"
                width="160"
                height="40"
                className="h-10 w-auto brightness-0 invert"
              />
              <div>
                <p className="font-bold text-white text-lg">Scholze-Kurz & Kurz</p>
                <p className="text-white/50 text-xs">Immobilien GmbH</p>
              </div>
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-5">
              Mittelständische, inhabergeführte Hausverwaltung für Wiesbaden, Mainz
              und das Rhein-Main-Gebiet — seit 1981.
            </p>
            <div className="bg-white/5 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-brand-gold" />
                <span className="text-white font-medium text-sm">Öffnungszeiten</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="text-white/50">Bürozeiten</p>
                  <p className="text-white">Mo–Fr 09–17 Uhr</p>
                </div>
                <div>
                  <p className="text-white/50">Telefonzeiten</p>
                  <p className="text-white">Mo–Fr 09–14 Uhr</p>
                </div>
              </div>
            </div>
          </div>

          {/* Leistungen - 2 cols */}
          <div className="col-span-1 lg:col-span-2">
            <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Leistungen</h4>
            <ul className="space-y-2.5">
              {[
                { name: 'WEG-Verwaltung', path: '/weg-verwaltung' },
                { name: 'Verwaltung wechseln', path: '/verwalter-wechseln' },
                { name: 'Großgemeinschaften ab 49 WE', path: '/weg-verwaltung/grossgemeinschaften' },
                { name: 'Mietverwaltung', path: '/mietverwaltung' },
                { name: 'Sondereigentum', path: '/sondereigentumsverwaltung' },
                { name: 'Öffentliche Liegenschaften', path: '/oeffentliche-verwaltung' },
                { name: 'Hausmeister & Winterdienst', path: '/facility' },
                { name: 'Renovieren/Sanieren', path: '/immobilien-sanieren-und-renovieren' },
                { name: 'Ankauf & Verkauf', path: '/ankauf-verkauf' },
                { name: 'Kostenrechner', path: '/kostenrechner', highlight: true },
                { name: 'Anfrage stellen', path: '/kontakt' },
              ].map((item) => (
                <li key={item.path}>
                  <Link to={item.path} className="text-white/60 hover:text-brand-gold transition-colors text-sm flex items-center gap-1 group">
                    <ChevronRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Unternehmen - 2 cols */}
          <div className="col-span-1 lg:col-span-2">
            <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Unternehmen</h4>
            <ul className="space-y-2.5">
              {[
                { name: 'Über uns', path: '/ueber-uns' },
                { name: 'Referenzen', path: '/referenzen' },
                { name: 'Aktuelles', path: '/aktuelles' },
                { name: 'Karriere', path: '/karriere', highlight: true },
                { name: 'Kontakt', path: '/kontakt' },
                { name: 'Mieterkontakt', path: '/mieterkontakt' },
                { name: 'Kundenportal', path: '/eigentuemerportal' },
                { name: 'Portal-Login', path: '/login' },
              ].map((item) => (
                <li key={item.path}>
                  <Link to={item.path} className={`${item.highlight ? 'text-brand-gold font-semibold' : 'text-white/60'} hover:text-brand-gold transition-colors text-sm flex items-center gap-1 group`}>
                    <ChevronRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {item.name}
                  </Link>
                </li>
              ))}
              <li>
                <a 
                  href="https://hvscholze-kurz.mycasavi.com/app/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-brand-gold transition-colors text-sm flex items-center gap-2"
                >
                  WEG-Portal <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            </ul>
          </div>

          {/* Büros - 5 cols (more space now) */}
          <div className="col-span-2 lg:col-span-5">
            <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Unsere Büros</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-white/5 rounded-lg p-4 hover:bg-white/10 transition-colors">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-brand-gold/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <MapPin className="w-4 h-4 text-brand-gold" />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">Hauptsitz</p>
                    <p className="text-white font-medium text-sm">Wiesbaden-Biebrich</p>
                    <p className="text-white/50 text-xs mt-1">Gaugasse 19</p>
                    <p className="text-white/50 text-xs">65203 Wiesbaden-Biebrich</p>
                    <a href="tel:+49611603313" className="text-white/50 hover:text-brand-gold text-xs transition-colors block mt-1">Tel: 0611 - 60 33 13</a>
                  </div>
                </div>
              </div>
              <div className="bg-white/5 rounded-lg p-4 hover:bg-white/10 transition-colors">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-brand-gold/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <MapPin className="w-4 h-4 text-brand-gold" />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">Geschäftsstelle</p>
                    <p className="text-white font-medium text-sm">Mainz-Kostheim</p>
                    <p className="text-white/50 text-xs mt-1">Hauptstraße 127</p>
                    <p className="text-white/50 text-xs">55246 Mainz-Kostheim</p>
                    <a href="tel:+4961342863266" className="text-white/50 hover:text-brand-gold text-xs transition-colors block mt-1">Tel: 06134 - 28 63 266</a>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <a href="tel:+49611603313" className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white py-2.5 rounded-lg text-sm font-medium transition-colors">
                <Phone className="w-4 h-4" /> Anrufen
              </a>
              <Link to="/kontakt" className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white py-2.5 rounded-lg text-sm font-medium transition-colors">
                <Mail className="w-4 h-4" /> Kontakt
              </Link>
            </div>
          </div>
        </div>

        {/* Mitgliedschaften & Verbände */}
        <div className="mt-10 pt-8 border-t border-white/10" data-testid="footer-memberships">
          <span className="text-white font-semibold text-sm uppercase tracking-wider block mb-4">
            Mitglied in
          </span>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* BVFI */}
            <a
              href="https://www.bvfi.de/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              title="Mitglied im BVFI · Bundesverband für die Immobilienwirtschaft e.V."
              aria-label="BVFI Mitgliedersiegel — Bundesverband für die Immobilienwirtschaft e.V."
              data-testid="bvfi-siegel-link"
              className="group flex items-center gap-4 bg-white/5 hover:bg-white/10 rounded-lg p-4 transition-colors"
            >
              <img
                src="/images/bvfi-siegel.png"
                alt="BVFI — Bundesverband für die Immobilienwirtschaft e.V."
                width="64"
                height="64"
                loading="lazy"
                className="w-16 h-16 object-contain bg-white rounded-full p-1 transition-transform duration-300 group-hover:scale-110"
              />
              <div className="min-w-0">
                <p className="text-[10px] font-bold text-brand-gold uppercase tracking-[0.15em]">Mitglied</p>
                <p className="text-white font-medium text-sm leading-tight">BVFI</p>
                <p className="text-white/50 text-xs mt-0.5">Bundesverband für die Immobilienwirtschaft e.V.</p>
              </div>
            </a>

            {/* Haus & Grund Hessen */}
            <a
              href="https://www.hausundgrundhessen.de/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              title="Mitglied · Haus & Grund Hessen"
              aria-label="Haus & Grund Hessen — Landesverband hessischer Haus-, Wohnungs- und Grundeigentümer"
              data-testid="haus-und-grund-hessen-link"
              className="group flex items-center gap-4 bg-white/5 hover:bg-white/10 rounded-lg p-4 transition-colors"
            >
              <div className="w-16 h-16 rounded-md bg-white flex items-center justify-center flex-shrink-0 p-1.5 transition-transform duration-300 group-hover:scale-110">
                <img
                  src="/images/haus-und-grund-hessen-logo.png"
                  alt="Haus & Grund — Eigentum. Schutz. Gemeinschaft."
                  width="52"
                  height="52"
                  loading="lazy"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-bold text-brand-gold uppercase tracking-[0.15em]">Mitglied</p>
                <p className="text-white font-medium text-sm leading-tight">Haus & Grund Hessen</p>
                <p className="text-white/50 text-xs mt-0.5">Landesverband hessischer Haus-, Wohnungs- und Grundeigentümer</p>
              </div>
            </a>

            {/* REEXPERTS */}
            <a
              href="https://reexperts.de/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              title="Mitglied · REEXPERTS · Vereinigung Freier Immobiliensachverständiger e.V."
              aria-label="REEXPERTS Verbandssiegel — Vereinigung Freier Immobiliensachverständiger"
              data-testid="reexperts-siegel-link"
              className="group flex items-center gap-4 bg-white/5 hover:bg-white/10 rounded-lg p-4 transition-colors"
            >
              <img
                src="/images/reexperts-siegel.png"
                alt="REEXPERTS — Vereinigung Freier Immobiliensachverständiger"
                width="64"
                height="64"
                loading="lazy"
                className="w-16 h-16 object-contain transition-transform duration-300 group-hover:scale-110"
              />
              <div className="min-w-0">
                <p className="text-[10px] font-bold text-brand-gold uppercase tracking-[0.15em]">Mitglied</p>
                <p className="text-white font-medium text-sm leading-tight">REEXPERTS e.V.</p>
                <p className="text-white/50 text-xs mt-0.5">Vereinigung Freier Immobiliensachverständiger</p>
              </div>
            </a>
          </div>
        </div>

        {/* Hausverwaltung & Vor Ort */}
        <div className="mt-10 pt-8 border-t border-white/10">
          <div className="mb-6">
            <span className="text-white font-semibold text-sm uppercase tracking-wider block mb-3">Hausverwaltung</span>
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-x-3 sm:gap-y-2">
              <Link to="/hausverwaltung-wiesbaden" className="text-white/60 hover:text-brand-gold transition-colors text-sm font-medium">
                Hausverwaltung Wiesbaden
              </Link>
              <span className="text-white/20 hidden sm:inline">&middot;</span>
              <Link to="/hausverwaltung-mainz" className="text-white/60 hover:text-brand-gold transition-colors text-sm font-medium">
                Hausverwaltung Mainz
              </Link>
              <span className="text-white/20 hidden sm:inline">&middot;</span>
              <Link to="/hausverwaltung-rhein-main" className="text-white/60 hover:text-brand-gold transition-colors text-sm font-medium">
                Hausverwaltung Rhein-Main
              </Link>
            </div>
          </div>
          <div>
            <span className="text-white font-semibold text-sm uppercase tracking-wider block mb-3">Vor Ort</span>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:flex md:flex-wrap md:items-center gap-x-4 gap-y-2">
              {[
                { name: 'Wiesbaden', path: '/hausverwaltung-wiesbaden' },
                { name: 'Mainz', path: '/hausverwaltung-mainz' },
                { name: 'Kostheim-Kastel', path: '/hausverwaltung-kostheim-kastel' },
                { name: 'Hochheim', path: '/hausverwaltung-hochheim' },
                { name: 'Flörsheim', path: '/hausverwaltung-floersheim' },
                { name: 'Idstein', path: '/hausverwaltung-idstein' },
                { name: 'Taunusstein', path: '/hausverwaltung-taunusstein' },
                { name: 'Eltville', path: '/hausverwaltung-eltville-rheingau' },
                { name: 'Bad Schwalbach', path: '/hausverwaltung-bad-schwalbach' },
                { name: 'Oppenheim', path: '/hausverwaltung-oppenheim' },
                { name: 'Rüsselsheim', path: '/hausverwaltung-ruesselsheim' },
              ].map((item) => (
                <Link key={item.path} to={item.path} className="text-white/50 hover:text-brand-gold transition-colors text-sm">
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Über uns Banner */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link
            to="/ueber-uns"
            data-testid="footer-ueber-uns-banner"
            className="group flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-gradient-to-r from-brand-gold/15 to-brand-gold/5 rounded-2xl px-6 sm:px-8 py-5 sm:py-6 hover:from-brand-gold/25 hover:to-brand-gold/10 transition-all duration-300"
          >
            <div className="flex items-start sm:items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-brand-gold/20 flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-0">
                <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-brand-gold" />
              </div>
              <div>
                <h4 className="text-white font-bold text-sm sm:text-lg leading-tight">Familienunternehmen seit 1981</h4>
                <p className="text-white/50 text-xs sm:text-sm mt-1">Lernen Sie unsere Geschichte kennen</p>
              </div>
            </div>
            <span className="flex items-center gap-2 text-brand-gold font-semibold text-sm group-hover:gap-3 transition-all whitespace-nowrap ml-13 sm:ml-0">
              Mehr erfahren
              <ChevronRight className="w-4 h-4" />
            </span>
          </Link>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-white/40">
            <p>© {currentYear} Scholze-Kurz & Kurz Immobilien GmbH – Alle Rechte vorbehalten</p>
            <div className="flex items-center gap-4">
              <Link to="/impressum" className="hover:text-brand-gold transition-colors">Impressum</Link>
              <span className="text-white/20">|</span>
              <Link to="/datenschutz" className="hover:text-brand-gold transition-colors">Datenschutz</Link>
              <span className="text-white/20">|</span>
              <Link to="/widerrufsbelehrung" className="hover:text-brand-gold transition-colors">Widerruf</Link>
              <span className="text-white/20">|</span>
              <Link to="/mietbewerbung" className="hover:text-brand-gold transition-colors" data-testid="footer-mietbewerbung-link">Mietbewerbung</Link>
              <span className="text-white/20">|</span>
              <span className="flex items-center gap-1">
                <span className="text-brand-gold">&#10038;</span>
                Seit 1981
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
    </>
  );
};

export default Footer;
